import { IndustryData } from './constants';
import FadeIn from '@/components/shared/FadeIn';
import SectionBackdrop from './SectionBackdrop';

interface IndustrySectionProps {
  industries: IndustryData[];
  onLearnMore: () => void;
}

const IndustrySection: React.FC<IndustrySectionProps> = ({ industries, onLearnMore }) => {
  return (
    <section id="industries" className="relative overflow-hidden bg-maintenance-dark py-20 md:py-28">
      <SectionBackdrop variant="blue-indigo" glowPosition="bottom-left" pattern="dots" />
      <div className="relative z-10 container mx-auto px-4 md:px-6">
        <FadeIn className="text-center mb-12 md:mb-16">
          <p className="text-maintenance-cyan text-xs uppercase tracking-widest font-semibold mb-3">
            Built for the Construction Industry
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-bold">
            A solution for every team
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {industries.map((industry, i) => {
            const Icon = industry.icon;
            return (
              <FadeIn key={industry.id} delay={i * 120}>
                <article
                  id={industry.id}
                  className="relative overflow-hidden rounded-2xl min-h-[480px] flex flex-col justify-end group"
                >
                {/* Background photo */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url('${industry.backgroundImage}')` }}
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-maintenance-navy via-maintenance-navy/60 to-transparent" />

                {/* Content */}
                <div className="relative z-10 p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white/10 rounded-lg p-2 shrink-0">
                      <Icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <h3 className={`text-xl md:text-2xl font-bold text-white`}>{industry.title}</h3>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {industry.content.map((item, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-2 text-white/70 text-sm md:text-base`}
                      >
                        <span className="mt-2 shrink-0 w-1 h-1 rounded-full bg-maintenance-blue" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={onLearnMore}
                    className={`inline-flex items-center gap-2 text-white/50 hover:text-white text-sm font-medium border-b border-white/20 hover:border-white transition-all pb-0.5`}
                  >
                    Learn More <span aria-hidden="true">→</span>
                  </button>
                </div>
                </article>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default IndustrySection;
